/**
 * Drag & drop du brain.
 *
 * Remplace `tokenOnDragStart`, `tokenOnDragEnd`, `tokenOnDragMove`,
 * `tokenCollideDropzone` et `checkCollision` de brain.js (~270 lignes utiles).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DIFFÉRENCE D'APPROCHE ASSUMÉE
 *
 * brain.js pilotait le visuel de façon impérative : à chaque `pointermove` il
 * écrivait des teintes sur les dropzones et les masques, puis `dragEnd` les
 * remettait toutes à zéro « au cas où » (le commentaire d'origine dit
 * exactement ça, brain.js:1574). Ce va-et-vient est la source des états
 * incohérents : il suffit qu'un chemin oublie une remise à zéro.
 *
 * Ici l'état visuel est DÉRIVÉ de l'état logique : une dropzone est en erreur
 * si et seulement si elle contient le token en cours de déplacement et que la
 * fusion est illégale. Aucune remise à zéro à écrire, donc aucune à oublier.
 * Le comportement observable est le même.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Ce qui n'est PAS porté : `checkCollisionCurrentTokenDropzones`
 * (brain.js:1793, 74 lignes) est du code mort, jamais appelé.
 */

import gsap from "gsap"
import { computed, reactive, ref } from "vue"

import { resolveTokenIdentity } from "@/features/brain/domain/assets"
import {
  centeredRect,
  childStageCenter,
  childStageRect,
  overlaps,
} from "@/features/brain/domain/collision"
import {
  calculateDropzoneValue,
  type BrainToken,
  type MergeResult,
  type NumericSum,
} from "@/features/brain/domain/fusion"
import {
  layoutScene,
  SPRITE_SIZE,
  type MaskLayout,
  type RawSceneData,
} from "@/features/brain/domain/geometry"

/** Taille du token pendant le déplacement — brain.js:1543. */
const DRAG_SPRITE_SIZE = 120

/** Durée du grossissement / retour à la taille normale — brain.js:1543, 1553. */
const RESIZE_DURATION = 0.35

/** Retour élastique à la position d'origine — brain.js:1556-1562. */
const RETURN_DURATION = 1.25
const RETURN_EASE = "elastic.out(1,0.3)"

/**
 * Courbe de l'opacité pendant ce retour — DIFFÉRENTE de celle de la position,
 * et c'est un écart assumé avec brain.js.
 *
 * `elastic.out` dépasse sa cible avant d'y revenir : c'est tout son intérêt
 * sur une position, et c'est un défaut sur une propriété bornée. Mesuré sur le
 * banc : l'opacité descend jusqu'à **-0,32** en cours de route, valeur que le
 * navigateur ramène à 0 — le token s'efface donc d'un coup, plus tôt et plus
 * brutalement que prévu, au lieu de fondre.
 *
 * brain.js appliquait bien la même élastique à `alpha` (1564-1571), et Pixi
 * borne pareillement. Le défaut existait donc déjà ; on ne le reproduit pas.
 */
const FADE_EASE = "power2.out"

/**
 * Opacité d'un token posé dans une dropzone (brain.js:1568).
 * Quasi invisible mais toujours saisissable : c'est la dropzone qui affiche
 * alors le résultat de la fusion, et l'utilisateur peut ressortir le token.
 */
const SETTLED_OPACITY = 0.005

export type TokenState = {
  key: string
  /** Nom épuré du marqueur "neg" — c'est celui que la fusion parse. */
  name: string
  /**
   * Nom tel qu'envoyé par l'API, marqueur "neg" compris. Sert à construire
   * l'URL de l'image : on ne peut pas la reconstituer depuis `name`, le
   * marqueur n'étant pas nécessairement en préfixe.
   */
  rawName: string
  type: string
  isNeg: boolean
  /** Position d'origine, pour le retour élastique. */
  baseX: number
  baseY: number
  x: number
  y: number
  /** Animé : 100 au repos, 120 pendant le déplacement. Compte dans la collision. */
  size: number
  opacity: number
  isDragging: boolean
  dropzoneKey: string | null
  /**
   * Rang d'empilement, croissant à chaque dépôt.
   *
   * brain.js:1571 appelait `bringToFront()` en posant un token dans une
   * dropzone : le dernier déposé passait au-dessus des précédents, et c'était
   * donc lui qu'on ressaisissait en premier. Sans ce rang, la pile suivrait
   * l'ordre des DONNÉES et non celui des dépôts — on retirerait les tokens
   * dans un ordre différent de celui où on les a posés.
   *
   * Rendu en `z-index` plutôt qu'en réordonnant le tableau : déplacer les
   * éléments ferait bouger les clés du `v-for` et recréerait des noeuds DOM
   * en plein glissement.
   */
  stackOrder: number
}

export type DropzoneState = {
  key: string
  name: string
  maskName: string
  localX: number
  localY: number
  /** Animé : le clignotement des dropzones vides descend à 80 (brain.js:1113). */
  size: number
  /** Tokens présents, dans l'ordre d'arrivée. */
  tokenKeys: string[]
}

export function useBrainInteraction(data: RawSceneData) {
  const layout = layoutScene(data)
  const masksByName = new Map<string, MaskLayout>(
    layout.masks.map((m) => [m.name, m])
  )

  const tokens = reactive<TokenState[]>(
    layout.tokens.map((t) => {
      const identity = resolveTokenIdentity(t.name)
      return {
        key: t.key,
        name: identity.name,
        rawName: t.name,
        type: t.type,
        isNeg: identity.isNeg,
        baseX: t.x,
        baseY: t.y,
        x: t.x,
        y: t.y,
        size: SPRITE_SIZE,
        opacity: 1,
        isDragging: false,
        dropzoneKey: null,
        stackOrder: 0,
      }
    })
  )

  const dropzones = reactive<DropzoneState[]>(
    layout.masks.flatMap((m) =>
      m.dropzones.map((d) => ({
        key: d.key,
        name: d.name,
        maskName: m.name,
        localX: d.x,
        localY: d.y,
        size: SPRITE_SIZE,
        tokenKeys: [] as string[],
      }))
    )
  )

  /** Nombre de déplacements effectifs, équivalent de `trackMoves`. */
  const moveCount = ref(0)

  const draggingKey = ref<string | null>(null)
  /** Dropzone du token déplacé au moment du clic, pour ne compter qu'un vrai changement. */
  let dropzoneAtDragStart: string | null = null

  const byKey = (key: string | null) =>
    key ? tokens.find((t) => t.key === key) : undefined
  const dropzoneByKey = (key: string | null) =>
    key ? dropzones.find((d) => d.key === key) : undefined

  // ────────────────────────────────────────────────────────────── géométrie

  function dropzoneRect(dz: DropzoneState) {
    const mask = masksByName.get(dz.maskName)!
    return childStageRect(mask, dz.localX, dz.localY, dz.size)
  }

  function dropzoneCenter(dz: DropzoneState) {
    const mask = masksByName.get(dz.maskName)!
    return childStageCenter(mask, dz.localX, dz.localY)
  }

  // ─────────────────────────────────────────────────────────────── fusion

  /** Les tokens d'une dropzone, dans la forme attendue par la fusion. */
  function dropzoneTokens(dz: DropzoneState): BrainToken[] {
    return dz.tokenKeys
      .map((k) => byKey(k))
      .filter((t): t is TokenState => Boolean(t))
      .map((t) => ({ name: t.name, type: t.type, isNeg: t.isNeg }))
  }

  /** Résultat de fusion courant de chaque dropzone, recalculé à la demande. */
  const merges = computed(() => {
    const out = new Map<string, MergeResult>()
    for (const dz of dropzones) {
      out.set(dz.key, calculateDropzoneValue(dropzoneTokens(dz)))
    }
    return out
  })

  /**
   * Nom de texture à afficher pour une dropzone, ou `null` pour l'image de
   * dropzone vide.
   *
   * brain.js ne changeait la texture que lorsque la fusion était légale
   * (1639-1682) : sur une fusion illégale, la dropzone garde son image
   * précédente et se contente de rougir.
   */
  function dropzoneTexture(dz: DropzoneState): {
    texture: string
    tokensType: string
    numericValue: number | null
  } | null {
    if (dz.tokenKeys.length === 0) return null

    const merge = merges.value.get(dz.key)
    if (!merge?.isLegal || !merge.texture || !merge.tokensType) return null

    const result = merge.result
    const numericValue =
      merge.tokensType === "numeric" && typeof result === "object"
        ? (result as NumericSum).n
        : null

    return {
      texture: merge.texture,
      tokensType: merge.tokensType,
      numericValue,
    }
  }

  // ───────────────────────────────────────────────── état visuel dérivé

  /** La dropzone survolée par le token en cours de déplacement, s'il y en a une. */
  const activeDropzoneKey = computed(
    () => byKey(draggingKey.value)?.dropzoneKey ?? null
  )

  /** La fusion en cours est-elle légale ? `null` si aucun survol. */
  const activeIsLegal = computed(() => {
    const key = activeDropzoneKey.value
    if (!key) return null
    return merges.value.get(key)?.isLegal === true
  })

  /** État de survol d'un masque : ni l'un ni l'autre, légal, ou illégal. */
  function maskHoverState(maskName: string): "idle" | "legal" | "illegal" {
    const key = activeDropzoneKey.value
    if (!key) return "idle"
    const dz = dropzoneByKey(key)
    if (!dz || dz.maskName !== maskName) return "idle"
    return activeIsLegal.value ? "legal" : "illegal"
  }

  /** Une dropzone est en erreur si elle porte la fusion illégale en cours. */
  function dropzoneIsError(dropzoneKey: string): boolean {
    return (
      activeDropzoneKey.value === dropzoneKey && activeIsLegal.value === false
    )
  }

  const allDropzonesFilled = computed(() =>
    dropzones.every((d) => d.tokenKeys.length > 0)
  )
  const anyDropzoneFilled = computed(() =>
    dropzones.some((d) => d.tokenKeys.length > 0)
  )

  // ─────────────────────────────────────────────────────────── interactions

  function detach(token: TokenState) {
    const dz = dropzoneByKey(token.dropzoneKey)
    if (dz) {
      const i = dz.tokenKeys.indexOf(token.key)
      if (i > -1) dz.tokenKeys.splice(i, 1)
    }
    token.dropzoneKey = null
  }

  function onDragStart(tokenKey: string) {
    const token = byKey(tokenKey)
    if (!token) return

    // Un token déposé part sur un retour élastique de 1,25 s qui anime x, y et
    // opacity. Le ressaisir pendant ce laps de temps sans tuer le tween donne
    // trois symptômes visibles : le token ne suit pas le pointeur (le tween
    // réécrit sa position à chaque frame), il s'efface (l'opacité continue de
    // descendre vers SETTLED_OPACITY), puis il saute sur la cible du tween à
    // la fin. Le cas est fréquent dès qu'une dropzone porte deux tokens :
    // on pose le second et on ressaisit le premier aussitôt.
    //
    // brain.js:1538 ne s'en protégeait pas non plus — on ne reproduit pas le
    // défaut.
    gsap.killTweensOf(token)

    draggingKey.value = tokenKey
    dropzoneAtDragStart = token.dropzoneKey
    token.isDragging = true
    token.opacity = 1

    gsap.to(token, {
      duration: RESIZE_DURATION,
      size: DRAG_SPRITE_SIZE,
      ease: "expo.out",
    })
  }

  /** `x` / `y` sont déjà convertis en px d'espace scène par l'appelant. */
  function onDragMove(x: number, y: number) {
    const token = byKey(draggingKey.value)
    if (!token?.isDragging) return

    token.x = x
    token.y = y
    token.opacity = 1

    const tokenRect = centeredRect(token.x, token.y, token.size)

    // Sortie de la dropzone courante
    const current = dropzoneByKey(token.dropzoneKey)
    if (current && !overlaps(tokenRect, dropzoneRect(current))) {
      detach(token)
    }

    // Entrée dans la première dropzone touchée (brain.js:1755-1769)
    if (token.dropzoneKey === null) {
      const hit = dropzones.find((dz) => overlaps(tokenRect, dropzoneRect(dz)))
      if (hit) {
        hit.tokenKeys.push(token.key)
        token.dropzoneKey = hit.key
        // La dropzone cesse de clignoter dès qu'on y dépose quelque chose.
        // brain.js:1758 appelait `.tw.kill()` sans garde : on passe par
        // stopBlink, qui vérifie l'existence du tween.
        stopBlink(hit)
      }
    }
  }

  function onDragEnd() {
    const token = byKey(draggingKey.value)
    draggingKey.value = null
    if (!token) return

    token.isDragging = false

    gsap.to(token, {
      duration: RESIZE_DURATION,
      size: SPRITE_SIZE,
      ease: "expo.out",
    })

    const dz = dropzoneByKey(token.dropzoneKey)
    const isLegal = dz ? merges.value.get(dz.key)?.isLegal === true : false

    if (dz && isLegal) {
      // Le token se cale sur la dropzone et s'effface : c'est elle qui montre
      // désormais le résultat de la fusion.
      // brain.js:1571 — le dernier déposé passe au-dessus.
      token.stackOrder = ++stackCounter

      const center = dropzoneCenter(dz)
      gsap.to(token, {
        duration: RETURN_DURATION,
        x: center.x,
        y: center.y,
        ease: RETURN_EASE,
      })
      // Tween séparé : l'opacité ne doit pas dépasser ses bornes, la position
      // le doit. Voir FADE_EASE.
      gsap.to(token, {
        duration: RETURN_DURATION,
        opacity: SETTLED_OPACITY,
        ease: FADE_EASE,
      })
    } else {
      // Pas de dropzone, ou fusion illégale : retour à la position d'origine
      // (brain.js:1555-1562 et 1592-1605).
      if (dz) detach(token)
      gsap.to(token, {
        duration: RETURN_DURATION,
        x: token.baseX,
        y: token.baseY,
        ease: RETURN_EASE,
      })
      gsap.to(token, {
        duration: RETURN_DURATION,
        opacity: 1,
        ease: FADE_EASE,
      })
    }

    if (token.dropzoneKey !== dropzoneAtDragStart) moveCount.value++
    dropzoneAtDragStart = null
  }

  /**
   * Place un token dans une dropzone par leur NOM, sans passer par le drag.
   *
   * C'est ce dont a besoin la démonstration du tutoriel (`playAnswer`,
   * brain.js:517-527) : elle déplace le token elle-même, puis l'inscrit dans
   * la dropzone pour déclencher la fusion. Les états visuels — texture
   * fusionnée, teinte — sont dérivés de là, il n'y a rien d'autre à écrire.
   *
   * Renvoie `false` si l'un des deux noms est inconnu, plutôt que de lever :
   * une donnée de tutoriel incohérente ne doit pas casser l'écran.
   */
  function placeToken(tokenName: string, dropzoneName: string): boolean {
    const token = tokens.find((t) => t.name === tokenName)
    const dz = dropzones.find((d) => d.name === dropzoneName)
    if (!token || !dz) return false

    detach(token)
    dz.tokenKeys.push(token.key)
    token.dropzoneKey = dz.key
    stopBlink(dz)
    return true
  }

  /** Croissant : chaque dépôt attribue un rang supérieur au précédent. */
  let stackCounter = 0

  /** Remet la scène dans son état initial — équivalent de `rawResetTest`. */
  function reset() {
    for (const dz of dropzones) dz.tokenKeys = []
    for (const token of tokens) {
      gsap.killTweensOf(token)
      token.dropzoneKey = null
      token.isDragging = false
      token.x = token.baseX
      token.y = token.baseY
      token.size = SPRITE_SIZE
      token.opacity = 1
      token.stackOrder = 0
    }
    draggingKey.value = null
    stackCounter = 0
  }

  // ──────────────────────────────────────────────────────── clignotement

  const blinkTweens = new Map<string, gsap.core.Tween>()

  /** brain.js:1113-1119 — attire l'œil sur les dropzones encore vides. */
  function startBlink(dz: DropzoneState) {
    stopBlink(dz)
    blinkTweens.set(
      dz.key,
      gsap.fromTo(
        dz,
        { size: 100 },
        { duration: 0.5, size: 80, yoyo: true, repeat: 7 }
      )
    )
  }

  function stopBlink(dz: DropzoneState) {
    blinkTweens.get(dz.key)?.kill()
    blinkTweens.delete(dz.key)
    dz.size = SPRITE_SIZE
  }

  /** brain.js:287-306 — fait clignoter toutes les dropzones encore vides. */
  function blinkEmptyDropzones() {
    for (const dz of dropzones) {
      if (dz.tokenKeys.length === 0) startBlink(dz)
    }
  }

  return {
    layout,
    tokens,
    dropzones,
    moveCount,
    draggingKey,
    // état dérivé
    merges,
    dropzoneTexture,
    maskHoverState,
    dropzoneIsError,
    allDropzonesFilled,
    anyDropzoneFilled,
    // géométrie
    dropzoneRect,
    dropzoneCenter,
    // actions
    onDragStart,
    onDragMove,
    onDragEnd,
    reset,
    blinkEmptyDropzones,
    stopBlink,
    placeToken,
  }
}

/** État d'interaction, tel que consommé par les composants. */
export type BrainInteraction = ReturnType<typeof useBrainInteraction>
